import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Col, Container, Row } from 'react-bootstrap';
import {
  Endpoints,
  Verbiage,
  loaderStyle,
  maxPageSize,
  positiveStatuses,
  PendingApproval,
  GenericError,
  UserId,
  ReduxAction,
} from '../../constants';

import smartHttp from '../../helpers/smartHttp';
import AgGridComponent from '../../sharedcomponents/ag-grid/AgGrid';
import ProductDistributorsDropdown from './ProductDistributorsDropdown';
import ProductsDropdown from './ProductsDropdown';
import PackagesDropdown from './PackagesDropdown';
import UsersDropdown from './UsersDropdown';
import Loader from './../../components/Loader';
import InfoIcon from './../../assets/icons/common/info.png';
import StickyTooltip from './../../sharedcomponents/StickyTooltip/StickyTooltip';
import StatusDefinitionHeader, {
  getStatusDefinition,
} from './StatusDefinitionHeader';
import {
  getSelectedContractNumbers,
  getDbConfigData,
} from '../../helpers/component-helper-methods';
import { Notify } from '../../sharedcomponents/Alert/Notify';
import store from './../../redux/store';

const DeleteEntitlements = memo(
  ({ contracts }) => {
    const getColumnClass = () => 'grid-cell-left';

    const formatUserCell = (rowData) => {
      if (!rowData) return '';
      const { contactName, emailId } = rowData;
      if (contactName && emailId) {
        return `${contactName} (${emailId})`;
      }
      return contactName || emailId || '';
    };

    const columns = [
      {
        headerName: 'User',
        headerTooltip: 'User',
        field: 'contactName',
        tooltipValueGetter: (params) => formatUserCell(params.data),
        valueGetter: (params) => formatUserCell(params.data),
        initialFlex: 4,
        initialHide: false,
        cellClass: getColumnClass,
        filter: false,
        suppressFilter: true,
      },
      {
        headerName: 'Package',
        headerTooltip: 'Package',
        field: 'packageName',
        tooltipField: 'packageName',
        initialFlex: 3,
        initialHide: false,
        cellClass: getColumnClass,
        filter: false,
        suppressFilter: true,
      },
      {
        headerName: 'Product',
        headerTooltip: 'Product',
        field: 'productName',
        tooltipField: 'productName',
        initialFlex: 3,
        initialHide: false,
        cellClass: getColumnClass,
        filter: false,
        suppressFilter: true,
      },
      {
        headerName: 'Delivery Platform (Type)',
        headerTooltip: 'Delivery Platform (Type)',
        field: 'deliveryPlatform',
        tooltipField: 'deliveryPlatform',
        initialFlex: 3,
        initialHide: false,
        cellClass: getColumnClass,
        valueGetter: (p) => {
          if (!p.data) return '';
          const { distributor, distributorPlatform, deliveryType } = p.data;
          return distributor && distributorPlatform && deliveryType
            ? `${distributor} : ${distributorPlatform}${deliveryType?.toLowerCase() !== 'web' ? ` (${deliveryType})` : ''}`
            : '';
        },
        filter: false,
        suppressFilter: true,
      },
      {
        headerName: 'Status',
        headerTooltip: 'Status',
        field: 'status',
        tooltipValueGetter: (params) => getStatusDefinition(params.value),
        headerComponent: StatusDefinitionHeader,
        initialFlex: 2,
        initialHide: false,
        cellClass: getColumnClass,
        cellRenderer: (params) => {
          const isTooltipVisible =
            tooltip.showTooltip && tooltip.rowId === params.rowIndex;
          return (
            <div style={{ position: 'relative' }} data-row-id={params.rowIndex}>
              <span>
                {params.value}
                {params.value === 'Blocked' && (
                  <img
                    src={InfoIcon}
                    className="info-icon"
                    style={{
                      cursor: 'pointer',
                      marginLeft: '5px',
                      width: '20px',
                    }}
                    title={params.data.blockedReason}
                    onClick={(event) => {
                      handleTooltipClick(
                        event,
                        params.rowIndex,
                        params.data.blockedReason,
                      );
                    }}
                  />
                )}
                {isTooltipVisible && (
                  <StickyTooltip
                    ref={tooltipRef}
                    text={tooltip.text}
                    onClose={() => setTooltip({})}
                  />
                )}
              </span>
            </div>
          );
        },
        filter: false,
        suppressFilter: true,
      },
    ];
    const gridRef = useRef();
    const tooltipRef = useRef(null);
    const gridContainerRef = useRef(null);
    const dispatch = useDispatch();
    const onDragStoppedHandler = (params) => {
      const columnDefs = columns;
      let groupCols = params.api.columnModel.rowGroupColumns;
      columnDefs.forEach(function (colDef) {
        if (groupCols.find((x) => x.colId === colDef.field)) {
          colDef.hide = true;
        } else {
          colDef.hide = false;
        }
      });
      gridRef.current.api.setGridOption('columnDefs', columnDefs);
    };

    const handleTooltipClick = (event, rowId, blockedReason) => {
      event.stopPropagation();
      if (tooltip.rowId === rowId) {
        setTooltip({});
      } else {
        setTooltip({ showTooltip: true, rowId, text: blockedReason });
      }
    };

    const defaultConfig = {
      rowModelType: 'serverSide',
      isExportCSV: false,
      isExportExcel: false,
      pivotPanelShow: '',
      pagination: false,
      isAutoSizeColums: true,
      enableCharts: false,
      sideBar: false,
      onDragStopped: onDragStoppedHandler,
      blockLoadDebounceMillis: 300,
      cacheBlockSize: 200,
      maxBlocksInCache: 1,
      overlayNoRowsTemplate: Verbiage.noData,
      rowGroupPanelShow: 'never', // disable row group panel
      suppressRowGroupPanel: true, // prevent drag here to set row group
    };

    const defaultColdef = {
      sortable: true,
      resizable: true,
      menuTabs: [],
      enableValue: false,
      enableRowGroup: false,
      rowGroup: false,
      suppressRowGroup: true,
      filter: false,
      suppressFilter: true,
      floatingFilter: false,
      suppressMenu: true,
    };

    const [loading, setLoading] = useState(false);
    const [packageDataSet, setPackageDataSet] = useState();
    const [productDataSet, setProductDataSet] = useState();
    const [distributorDataSet, setDistributorDataSet] = useState();
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedDistributorsId, setSelectedDistributorsId] = useState([]);
    const [filteredPackages, setFilteredPackages] = useState();
    const [filteredProducts, setFilteredProducts] = useState();
    const [filteredDistributors, setFilteredDistributors] = useState();
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [serverRowCount, setServerRowCount] = useState(0);
    const [tooltip, setTooltip] = useState({});
    const [dropdownResetKey, setDropdownResetKey] = useState(0);
    const [isProductDropdownDisabled, setIsProductDropdownDisabled] =
      useState(false);
    const [isPackageDropdownDisabled, setIsPackageDropdownDisabled] =
      useState(false);
    const [selectedParentCascade, setSelectedParentCascade] = useState();
    const [uniqAvailableMappings, setUniqAvailableMappings] = useState();
    const [deleteThresholdLimit, setDeleteThresholdLimit] = useState();

    useEffect(() => {
      (async () => {
        let storeDBConfig = await getDbConfigData(dispatch);
        if (storeDBConfig.deleteEntitlementThreshold) {
          setDeleteThresholdLimit(
            Number(storeDBConfig.deleteEntitlementThreshold || 0),
          );
        }
      })();
    }, []);

    useEffect(() => {
      const resetTimeout = setTimeout(() => {
        setFilteredPackages();
        setFilteredProducts();
        setFilteredDistributors();
        setSelectedPackages([]);
        setSelectedProducts([]);
        setSelectedDistributorsId([]);
        setSelectedUsers([]);
        getAllUserEntitlements();
      }, 1000);
      return () => clearTimeout(resetTimeout);
    }, [contracts]);

    useEffect(() => {
      if (packageDataSet) setFilteredPackages(packageDataSet);
      if (productDataSet) setFilteredProducts(productDataSet);
      if (distributorDataSet) setFilteredDistributors(distributorDataSet);
    }, [packageDataSet, productDataSet, distributorDataSet]);

    useEffect(() => {
      setIsProductDropdownDisabled(selectedPackages.length > 0);
    }, [selectedPackages]);

    useEffect(() => {
      setIsPackageDropdownDisabled(selectedProducts.length > 0);
    }, [selectedProducts]);

    // Filter distributors based on product selection
    useEffect(() => {
      if (
        selectedParentCascade === 'ProductDropdown' &&
        uniqAvailableMappings
      ) {
        let prodId = [];
        selectedProducts.forEach((el) => {
          prodId.push(el.productId);
        });
        let filterData = uniqAvailableMappings.filter((el) =>
          prodId.includes(el.productId),
        );
        // Ensure all required fields have values
        const safeFilterData = filterData.map((item) => ({
          ...item,
          distributorDisplayName: item.distributorDisplayName || '',
          distributorPlatformDisplayName:
            item.distributorPlatformDisplayName || '',
          deliveryType: item.deliveryType || '',
          platformCategories: item.platformCategories || '',
        }));
        setFilteredDistributors({ results: safeFilterData });
      }
    }, [selectedProducts]);

    // Filter products based on distributor selection
    useEffect(() => {
      if (
        selectedParentCascade === 'DistributorDropdown' &&
        uniqAvailableMappings
      ) {
        let filterData = uniqAvailableMappings
          .filter((el) => {
            return (
              selectedDistributorsId.includes(el.distributorId) &&
              el.productId &&
              el.productName
            );
          })
          .map((item) => ({
            ...item,
            productName: item.productName || '',
          }));
        setFilteredProducts({ results: filterData });
      }
    }, [selectedDistributorsId]);

    // Filter packages based on distributor selection
    useEffect(() => {
      if (
        selectedParentCascade === 'DistributorDropdown' &&
        uniqAvailableMappings
      ) {
        let filterData = uniqAvailableMappings
          .filter((el) => {
            return (
              selectedDistributorsId.includes(el.distributorId) &&
              el.packageId &&
              el.packageName
            );
          })
          .map((item) => ({
            ...item,
            packageName: item.packageName || '',
          }));
        setFilteredPackages({ results: filterData });
      }
    }, [selectedDistributorsId]);

    // Filter distributors based on package selection
    useEffect(() => {
      if (
        selectedParentCascade === 'PackageDropdown' &&
        uniqAvailableMappings
      ) {
        let pkgId = [];
        selectedPackages.forEach((el) => {
          pkgId.push(el.packageId);
        });
        let filterData = uniqAvailableMappings.filter((el) =>
          pkgId.includes(el.packageId),
        );
        // Ensure all required fields have values
        const safeFilterData = filterData.map((item) => ({
          ...item,
          distributorDisplayName: item.distributorDisplayName || '',
          distributorPlatformDisplayName:
            item.distributorPlatformDisplayName || '',
          deliveryType: item.deliveryType || '',
          platformCategories: item.platformCategories || '',
        }));
        setFilteredDistributors({ results: safeFilterData });
      }
    }, [selectedPackages]);

    // Cascading dropdown logic
    const cascadingProductDistributor = () => {
      // On empty selection, set to default data
      if (
        selectedProducts.length === 0 &&
        selectedParentCascade === 'ProductDropdown'
      ) {
        setFilteredDistributors(distributorDataSet);
      } else if (
        selectedDistributorsId.length === 0 &&
        selectedParentCascade === 'DistributorDropdown'
      ) {
        setFilteredProducts(productDataSet);
        setFilteredPackages(packageDataSet);
      } else if (
        selectedPackages.length === 0 &&
        selectedParentCascade === 'PackageDropdown'
      ) {
        setFilteredDistributors(distributorDataSet);
      }

      // Cascading functionality to set which dropdown is parent
      if (
        selectedProducts.length === 0 &&
        selectedDistributorsId.length === 0 &&
        selectedPackages.length === 0
      ) {
        setSelectedParentCascade();
      } else if (!selectedParentCascade) {
        if (selectedProducts.length > 0) {
          setSelectedParentCascade('ProductDropdown');
        }
        if (selectedDistributorsId.length > 0) {
          setSelectedParentCascade('DistributorDropdown');
        }
        if (selectedPackages.length > 0) {
          setSelectedParentCascade('PackageDropdown');
        }
      }
    };

    // Memoized selection to stop loop from dropdown callback
    useMemo(cascadingProductDistributor, [
      selectedProducts,
      selectedDistributorsId,
      selectedPackages,
    ]);

    useEffect(() => {
      if (tooltip.showTooltip && gridContainerRef.current) {
        const scrollContainer =
          gridContainerRef.current.querySelector('.ag-body-viewport');
        const handleScroll = () => {
          const rowElement = document.querySelector(
            `[data-row-id="${tooltip.rowId}"]`,
          );
          if (rowElement) {
            const gridRect = scrollContainer.getBoundingClientRect();
            const rowRect = rowElement.getBoundingClientRect();
            if (
              rowRect.bottom < gridRect.top ||
              rowRect.top > gridRect.bottom
            ) {
              setTooltip({});
            }
          } else {
            setTooltip({});
          }
        };
        if (scrollContainer)
          scrollContainer.addEventListener('scroll', handleScroll);
        return () =>
          scrollContainer &&
          scrollContainer.removeEventListener('scroll', handleScroll);
      }
    }, [tooltip.showTooltip, tooltip.rowId]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target) &&
          !event.target.classList.contains('info-icon')
        ) {
          setTooltip({});
        }
      };
      if (tooltip.showTooltip) {
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('click', handleClickOutside);
      }
      return () => document.removeEventListener('click', handleClickOutside);
    }, [tooltip.showTooltip]);

    const getAllUserEntitlements = async () => {
      try {
        if (!contracts?.length || !contracts.some((el) => el.checked)) {
          setPackageDataSet([]);
          setProductDataSet([]);
          setDistributorDataSet([]);
          return;
        }
        const { contractNumbers, contractNumberFilter } =
          getSelectedContractNumbers(contracts);
        if (!contractNumbers.length) {
          setPackageDataSet([]);
          setProductDataSet([]);
          setDistributorDataSet([]);
          return;
        }
        const packageFields =
          'packageId,packageName,componentCount,distributor,distributorDisplayName,distributorPlatformDisplayName,distributorId,distributorPlatform,deliveryType,productComponentId,platformCategories';
        const productFields =
          'productId,productName,distributor,distributorDisplayName,distributorPlatformDisplayName,distributorId,distributorPlatform,deliveryType,productComponentId,platformCategories,productType';

        const distributorFilter = 'distributor in ("AlphaMail")';
        // FEATURE DISABLED (Release july-2026)
        // const productTypeFilter = 'productType in ("Ancillary","Legacy Product")';
        const packageDistributorFilter = 'distributor not in ("AlphaMail")';

        setLoading(true);
        const [packageRes, productRes, entitlementRes] = await Promise.all([
          smartHttp.get(Endpoints.contractPackagesApi, {
            pageSize: maxPageSize,
            id: Date.now(),
            filter: `contractNumber in (${contractNumberFilter}) and ${packageDistributorFilter}`,
            Field: packageFields,
          }),

          smartHttp.get(Endpoints.contractProductsApi, {
            pageSize: maxPageSize,
            id: Date.now(),
            // FEATURE DISABLED (Release july-2026) - removed OR producttype in (...)
            filter: `contractNumber in (${contractNumberFilter}) and ${distributorFilter}`,
            Field: productFields,
          }),

          smartHttp.get(Endpoints.userentitlementsApi, {
            pageSize: maxPageSize,
            id: Date.now(),
            Field:
              'distributor,distributorId,distributorPlatform,distributorDisplayName,distributorPlatformDisplayName,deliveryType',
            filter: `contractNumber in (${contractNumberFilter}) AND status IN(${positiveStatuses
              .map((s) => `"${s}"`)
              .join(',')})`,
          }),
        ]);
        const filteredContractPackages = packageRes?.data?.results || [];
        const filteredContractProducts = productRes?.data?.results || [];
        const filteredEntitlementData = entitlementRes?.data?.results || [];

        // Get distributor IDs that already have entitlements
        const entitlementDistributorIds = new Set(
          filteredEntitlementData.map(({ distributorId }) => distributorId),
        );

        // Add indicatePlatform flag to packages and products
        const packagesWithIndicator = filteredContractPackages.map((item) => ({
          ...item,
          indicatePlatform: entitlementDistributorIds.has(item.distributorId),
        }));

        const productsWithIndicator = filteredContractProducts.map((item) => ({
          ...item,
          indicatePlatform: entitlementDistributorIds.has(item.distributorId),
        }));

        const packageData = [
          ...new Map(
            packagesWithIndicator.map((item) => [
              item.packageId,
              {
                ...item,
                packageName: item.packageName || '',
              },
            ]),
          ).values(),
        ];
        const productData = [
          ...new Map(
            productsWithIndicator.map((item) => [
              item.productId,
              {
                ...item,
                productName: item.productName || '',
              },
            ]),
          ).values(),
        ];
        setPackageDataSet({
          metadata: {
            ...(packageRes?.data?.metadata || {}),
            count: packageData.length,
          },
          results: packageData,
        });
        setProductDataSet({
          metadata: {
            ...(productRes?.data?.metadata || {}),
            count: productData.length,
          },
          results: productData,
        });
        // Fetch company-distributor-mapping to filter dropdown
        const hcAccountId = contracts?.find((c) => c.checked)?.hC_AccountID;
        let mappedIdSet = new Set();
        if (hcAccountId) {
          try {
            const mappingRes = await smartHttp.getRapid(
              Endpoints.companyDistributorMappingApi,
              {
                pageSize: 5000,
                filter: `hcaccountid:"${hcAccountId}" AND isdeleted:0`,
              },
            );
            const mappedIds = (mappingRes?.data?.results || [])
              .map((m) => m.distributorId ?? m.distributorid)
              .filter((id) => id !== undefined && id !== null);
            mappedIdSet = new Set(mappedIds);
          } catch {
            /* ignore mapping errors, show all distributors */
          }
        }

        // Combine packages and products for distributors dropdown with indicator
        const combinedDistributors = [
          ...packagesWithIndicator,
          ...productsWithIndicator,
        ]
          .map((item) => ({
            ...item,
            distributorDisplayName: item.distributor || '',
            distributorPlatformDisplayName: item.distributorPlatform || '',
            deliveryType: item.deliveryType || '',
            platformCategories: item.platformCategories || '',
          }))
          .filter(
            (item) =>
              mappedIdSet.size === 0 || mappedIdSet.has(item.distributorId),
          );

        // Create unique package-product-distributor mappings for cascading
        const uniquePkgPrdDist = Array.from(
          new Map(
            combinedDistributors.map((item) => [
              `${item.packageId}_${item.productId}_${item.distributorId}`,
              item,
            ]),
          ).values(),
        );
        setUniqAvailableMappings(uniquePkgPrdDist || []);

        setDistributorDataSet({
          metadata: {
            count: combinedDistributors.length,
          },
          results: combinedDistributors,
        });
      } catch (err) {
        setPackageDataSet([]);
        setProductDataSet([]);
        setDistributorDataSet([]);
      } finally {
        setLoading(false);
      }
    };

    // Build filter directly from state
    const buildFilterFromState = () => {
      const { contractNumbers, contractNumberFilter } =
        getSelectedContractNumbers(contracts || []);
      if (!contractNumbers.length) {
        return null;
      }

      const clauses = [`contractNumber IN(${contractNumberFilter})`];

      // Add user filter
      if (selectedUsers.length > 0) {
        const userIds = selectedUsers
          .map((u) => u.contactId)
          .filter(Boolean)
          .map((id) => `"${id}"`)
          .join(',');
        if (userIds) {
          clauses.push(`contactid IN(${userIds})`);
        }
      }

      // Add distributor filter
      if (selectedDistributorsId.length > 0) {
        const distIds = selectedDistributorsId.filter(Boolean).join(',');
        if (distIds) {
          clauses.push(`distributorId IN(${distIds})`);
        }
      }

      // Add package/product filter via packageId/productId
      const packageIds = selectedPackages
        .map((pkg) => pkg.packageId)
        .filter(Boolean);
      if (packageIds.length > 0) {
        clauses.push(`packageId IN(${packageIds.join(',')})`);
      }

      const productIds = selectedProducts
        .map((product) => product.productId)
        .filter(Boolean);
      if (productIds.length > 0) {
        clauses.push(`productId IN(${productIds.join(',')})`);
      }

      // Status filter (fixed set) - include Pending Approval for delete page
      const deleteStatuses = [...positiveStatuses, PendingApproval];
      const statusFilter = deleteStatuses.map((s) => '"' + s + '"').join(',');
      clauses.push(`status IN(${statusFilter})`);

      const finalFilter = clauses.join(' AND ');
      return finalFilter;
    };

    const toCsv = (values) => {
      const cleaned = (values || [])
        .map((value) =>
          value !== undefined && value !== null ? String(value).trim() : '',
        )
        .filter(Boolean);
      return cleaned.length ? cleaned.join(',') : undefined;
    };

    const buildDeletePayload = () => {
      const { contractNumbers } = getSelectedContractNumbers(contracts || []);
      const contractNumbersCsv = toCsv(contractNumbers);
      const contactIdsCsv = toCsv(selectedUsers.map((user) => user?.contactId));
      const packageIdsCsv = toCsv(
        selectedPackages.map((pkg) => pkg?.packageId),
      );
      const productIdsCsv = toCsv(
        selectedProducts.map((product) => product?.productId),
      );
      const distributorIdsCsv = toCsv(selectedDistributorsId);

      const payload = {
        contractNumbers: contractNumbersCsv,
        contactIds: contactIdsCsv,
        packageIds: packageIdsCsv,
        productIds: productIdsCsv,
        distributorIds: distributorIdsCsv,
      };

      return Object.fromEntries(
        Object.entries(payload).filter(([, value]) => Boolean(value)),
      );
    };

    useEffect(() => {
      if (gridRef.current?.api) {
        // Use setTimeout to avoid redrawing while drawing
        const timeoutId = setTimeout(() => {
          try {
            gridRef.current?.api?.setGridOption(
              'serverSideDatasource',
              getServerSideDatasource(),
            );
          } catch (err) {
            // Error setting datasource
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }, [
      selectedUsers,
      selectedDistributorsId,
      selectedPackages,
      selectedProducts,
    ]);

    const getServerSideDatasource = () => ({
      getRows: (params) => {
        try {
          const hasSelection =
            selectedUsers.length > 0 ||
            selectedPackages.length > 0 ||
            selectedProducts.length > 0 ||
            selectedDistributorsId.length > 0;

          if (!hasSelection) {
            params.success({ rowData: [], rowCount: 0 });
            setServerRowCount(0);
            gridRef.current?.api?.showNoRowsOverlay();
            return;
          }

          const filterClause = buildFilterFromState();
          if (!filterClause) {
            params.success({ rowData: [], rowCount: 0 });
            setServerRowCount(0);
            gridRef.current?.api?.showNoRowsOverlay();
            return;
          }

          const AgParams = params.request;
          const sortParts = [];
          const seenSortColumns = new Set();
          (AgParams.sortModel || []).forEach((sort) => {
            if (!sort.colId || !sort.sort) return;
            seenSortColumns.add(String(sort.colId).toLowerCase());
            sortParts.push(`${sort.colId}:${sort.sort}`);
          });

          const fallbackSorts = [
            { colId: 'statusSortOrder', sort: 'asc' },
            { colId: 'modifiedDate', sort: 'desc' },
            { colId: 'userEntitlementId', sort: 'desc' },
          ];
          fallbackSorts.forEach(({ colId, sort }) => {
            if (!seenSortColumns.has(colId.toLowerCase())) {
              sortParts.push(`${colId}:${sort}`);
            }
          });

          const sortModel = sortParts.join(',');

          const queryParams = {
            id: String(Date.now()),
            PageSize: AgParams.endRow - AgParams.startRow,
            Page: AgParams.startRow / (AgParams.endRow - AgParams.startRow) + 1,
            Field:
              'contactId,contractNumber,contactName,emailId,packageName,productName,userEntitlementId,distributor,distributorPlatform,deliveryType,distributorId,productComponentId,status,statusSortOrder,modifiedDate,groupId,groupName',
            Filter: filterClause,
            Sort: sortModel,
          };

          params.api.showLoadingOverlay();

          smartHttp
            .get(Endpoints.userentitlementsApi, queryParams, {
              useCache: false,
            })
            .then(({ data }) => {
              const raw = data?.results || [];
              const results = raw.map((r) => ({
                ...r,
                deliveryPlatform:
                  r.distributor && r.distributorPlatform && r.deliveryType
                    ? `${r.distributor} : ${r.distributorPlatform}${r.deliveryType?.toLowerCase() !== 'web' ? ` (${r.deliveryType})` : ''}`
                    : '',
              }));
              const count = data?.metadata?.count || results.length;
              params.success({ rowData: results, rowCount: count });
              setServerRowCount(count);
              if (!results.length) {
                gridRef.current?.api?.showNoRowsOverlay();
              } else {
                gridRef.current?.api?.hideOverlay();
              }
            })
            .catch((err) => {
              params.fail();
              setServerRowCount(0);
              gridRef.current?.api?.hideOverlay();
            });
        } catch (e) {
          params.fail();
          setServerRowCount(0);
          gridRef.current?.api?.hideOverlay();
        }
      },
    });

    const afterDeleteSuccess = () => {
      setSelectedUsers([]);
      setSelectedPackages([]);
      setSelectedProducts([]);
      setSelectedDistributorsId([]);
      setDropdownResetKey((key) => key + 1);
      setServerRowCount(0);
    };
    const onGridReadyHandler = (params) => {
      gridRef.current = params;
      params.api.setGridOption('serverSideDatasource', getServerSideDatasource());
      setTimeout(() => params.api.hideOverlay(), 50);
    };

    // Update dropdown callbacks
    const callBackUsers = (users) => {
      setSelectedUsers(users || []);
    };
    const callBackPackages = (packages) => {
      setSelectedPackages(packages || []);
    };
    const callBackProducts = (products) => {
      setSelectedProducts(products || []);
    };
    const callBackDistributors = (params) => {
      let dstsId = [];
      params.forEach((dst) => {
        dstsId.push(dst.distributorId);
      });
      setSelectedDistributorsId(dstsId);
    };

    const onDeleteHandler = () => {
      const filterClause = buildFilterFromState();
      const deletePayload = buildDeletePayload();

      const queryParams = {
        id: String(Date.now()),
        PageSize: maxPageSize,
        Field:
          'contactId,contactName,emailId,packageName,productName,userEntitlementId,groupId,groupName',
        Filter: filterClause,
        Sort: 'groupName:asc,contactName:asc',
      };

      setLoading(true);
      smartHttp
        .get(Endpoints.userentitlementsApi, queryParams)
        .then(({ data }) => {
          const results = data?.results || [];
          const entitlementsWithGroup = results.filter(
            (e) => e.groupId && e.groupName,
          );

          if (entitlementsWithGroup.length > 0) {
            Notify({
              alert: true,
              size: 'md',
              type: 'warning',
              title:
                'Some entitlements are tagged to a group and so cannot be deleted from here. Please navigate to the My Groups page and make necessary changes.',
              confirmBtnText: 'OK',
              dialogClassName: 'group-entitlement-warning',
            });
            return;
          }
          const totalToDelete = results.length;
          const confirmTitle =
            totalToDelete === 1
              ? 'Are you sure you want to delete 1 entitlement?'
              : `Are you sure you want to delete ${totalToDelete} entitlements?`;

          if (serverRowCount <= deleteThresholdLimit) {
            const proceedWithDelete = () => {
              setLoading(true);
              smartHttp
                .post(Endpoints.bulkDeleteUserEntitlementsApi, deletePayload)
                .then(({ data: deleteData }) => {
                  const updatedCount = deleteData?.results?.updatedCount || 0;
                  if (updatedCount === 0) {
                    Notify({
                      alert: true,
                      type: 'warning',
                      title: 'No entitlements matched the selected criteria.',
                    });
                    return;
                  }

                  smartHttp.clearCache(Endpoints.userentitlementsApi);

                  Notify({
                    alert: true,
                    type: 'success',
                    title: `${updatedCount} entitlement${
                      updatedCount > 1 ? 's' : ''
                    } deleted.`,
                  });

                  afterDeleteSuccess();
                })
                .catch(() => {
                  Notify({
                    alert: true,
                    type: 'error',
                    title: GenericError.somethingWentWrong,
                  });
                })
                .finally(() => {
                  setLoading(false);
                });
            };

            Notify({
              alert: true,
              type: 'warning',
              title: confirmTitle,
              confirmBtnText: 'Yes',
              cancelBtnText: 'No',
              onConfirm: proceedWithDelete,
            });
            return;
          } else {
            const requestBody = {
              apirequestid: -1,
              payloadcount: serverRowCount,
              apiname: Endpoints.bulkDeleteUserEntitlementsApi,
              requestBody: deletePayload,
              UserId: UserId,
              source: 'Entitlement',
              hcname:
                store.getState()?.contract?.contracts[0]?.homogenizedCompany ||
                '',
            };
            const proceedWithThresholdDelete = () => {
              onDeleteThresholdHandler(requestBody);
            };

            Notify({
              alert: true,
              type: 'warning',
              title: confirmTitle,
              confirmBtnText: 'Yes',
              cancelBtnText: 'No',
              onConfirm: proceedWithThresholdDelete,
            });
            return;
          }
        })
        .catch((err) => {
          // Error checking groups
          Notify({
            alert: true,
            type: 'error',
            title: GenericError.somethingWentWrong,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const onDeleteThresholdHandler = async (requestBody) => {
      const resp = await smartHttp.post(Endpoints.apiRequestApi, requestBody);
      if (resp?.data?.code === 0) {
        Notify({
          alert: true,
          type: 'success',
          title:
            'The request is above allowed threshold for deletion. Your request is being reviewed internally',
        });
        sendEmailToInvites(resp);
        afterDeleteSuccess();
      }
    };

    const getEmailTemplateDetails = async () => {
      let tempEmailInfo = {};
      let queryParams = {
        keyItem: `Entitlement_Deletion_Pending_Approval_Email_Template`,
      };

      await smartHttp
        .get(Endpoints.configurationApi, queryParams)
        .then((res) => {
          const response = res?.data?.results;
          response.map((item) => {
            if (
              item.keyItem ===
              'Entitlement_Deletion_Pending_Approval_Email_Template'
            ) {
              let parseList = JSON.parse(item.keyValue);

              tempEmailInfo.emailDetails = parseList;
            }
          });
          dispatch({
            type: ReduxAction.emailDetails,
            payload: tempEmailInfo,
          });
        })
        .catch((err) => {});
      return tempEmailInfo.emailDetails;
    };

    const sendEmailToInvites = async (params) => {
      let emailDetails = await getEmailTemplateDetails();
      if (emailDetails) {
        let emailData = [];
        let storeData = store.getState();
        let caseNo = params?.data?.results?.apiRequestId ?? '';
        let storeUserInfo = storeData?.userInfo;
        let hcname =
          storeData?.contract?.contracts[0]?.homogenizedCompany ?? '';
        let currentUrl = window.location.href;
        let mail_from_address = 'donotreply@spglobal.com';
        let mail_body = emailDetails.MAIL_BODY.replace(
          '$Requestor_Email_ID$',
          storeUserInfo?.userInfo?.UserID,
        )
          .replace('$Homogenized_Company_Name$', hcname)
          .replace('$Login_Url$', currentUrl)
          .replace('$Case_Number$', caseNo);

        emailData.push({
          MAIL_FROM_ADDRESS: mail_from_address,
          MAIL_TO_ADDRESS: emailDetails.MAIL_TO_ADDRESS,
          MAIL_CC_ADDRESS: emailDetails.MAIL_CC_ADDRESS,
          MAIL_BODY: mail_body,
          MAIL_SUBJECT: emailDetails.MAIL_SUBJECT,
        });
        smartHttp
          .post(Endpoints.sendEmailApi, btoa(JSON.stringify(emailData[0])))
          .then((response) => {
            if (response.status === 200) {
            }
          })
          .catch((err) => {
            Notify({
              alert: true,
              type: 'error',
              title: GenericError.somethingWentWrong,
            });
          });
      }
    };

    const hasAnySelection =
      selectedUsers.length > 0 ||
      selectedPackages.length > 0 ||
      selectedProducts.length > 0 ||
      selectedDistributorsId.length > 0;
    const isGridDataAvailable = hasAnySelection;
    return (
      <>
        {loading && <Loader type="scaleLoader" cssClass={loaderStyle} />}
        <style>{`.block-cursor { cursor: not-allowed !important; pointer-events: none; opacity: 0.6; }`}</style>

        <Container fluid>
          <Row className="mt-2">
            <Col>
              <UsersDropdown
                key={`users-${dropdownResetKey}`}
                contracts={contracts}
                callBackUsers={callBackUsers}
                isGroupFilterApply={true}
                enableSelectAll={false}
                entitledOnly={false}
              />
            </Col>
            <Col className={isPackageDropdownDisabled ? 'block-cursor' : ''}>
              <PackagesDropdown
                key={`packages-${dropdownResetKey}`}
                DataSet={filteredPackages}
                callBackPackages={callBackPackages}
                enableSelectAll={false}
              />
            </Col>
            <Col className={isProductDropdownDisabled ? 'block-cursor' : ''}>
              <ProductsDropdown
                key={`products-${dropdownResetKey}`}
                DataSet={filteredProducts}
                callBackProducts={callBackProducts}
                enableSelectAll={false}
              />
            </Col>
            <Col>
              <ProductDistributorsDropdown
                key={`distributors-${dropdownResetKey}`}
                DataSet={filteredDistributors}
                callBackDistributors={callBackDistributors}
                enableSelectAll={false}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="ag-grid-add-entitlement-table">
                <div
                  className={
                    isGridDataAvailable ? 'd-none' : 'd-block starter-text'
                  }
                >
                  <h4>Select criteria</h4>
                  <p>
                    Select and filter by Users / Packages / Products / Delivery
                    Platform to view and delete existing entitlements.
                  </p>
                </div>
                {isGridDataAvailable && (
                  <span title="Currently subscribed by other users">
                    🟢 Currently subscribed by other users
                  </span>
                )}
                <div
                  className={
                    isGridDataAvailable
                      ? 'd-block ag-grid-add-entitlement-table'
                      : 'd-none'
                  }
                  ref={gridContainerRef}
                >
                  <AgGridComponent
                    /* autoGroupColumnDef removed to disable grouping */
                    config={defaultConfig}
                    defaultColumnDef={defaultColdef}
                    columns={columns}
                    getRowId={(p) =>
                      p.data?.userEntitlementId
                        ? 'ent-' + p.data.userEntitlementId
                        : p.data?.contactId + '_' + p.data?.productComponentId
                    }
                    onGridReady={onGridReadyHandler}
                  />
                  <Col className="float-end mt-2 mb-2">
                    <button
                      className="btn btn-dark btn-opacity"
                      onClick={onDeleteHandler}
                      disabled={!serverRowCount}
                    >
                      Proceed
                    </button>
                  </Col>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  },
  (op, np) => op?.contracts === np?.contracts && op?.selectedUsers === np?.selectedUsers,
);

export default DeleteEntitlements;
